import { getRouteApi, Link, useNavigate } from "@tanstack/react-router";
import {
  ArrowLeft,
  Calendar,
  ChevronDown,
  DollarSign,
  Flag,
  HelpCircle,
  Loader2,
  Lock,
  Search,
  Users,
  X,
} from "lucide-react";
import { Suspense, useEffect, useMemo, useState } from "react";
import { AdPlacement } from "@/components/ads/AdPlacement";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { m } from "@/paraglide/messages";
import { AlgorithmSelector } from "../components/AlgorithmSelector";
import {
  BudgetBuilderDesktopContentSkeleton,
  BudgetBuilderMobileContentSkeleton,
} from "../components/BudgetBuilderSkeletons";
import { BudgetCompactTable } from "../components/BudgetCompactTable";
import { BudgetVerdictCard } from "../components/BudgetVerdictCard";
import { F1Explainer } from "../components/F1Explainer";
import { MobileTabBar } from "../components/mobile";
import { SeasonRaceSelector } from "../components/SeasonRaceSelector";
import {
  type BudgetBuilderStage,
  type Race,
  useRaceStages,
  useRaces,
  useTeamCalculatorData,
} from "../hooks/use-f1-data";
import { useMyTeamVault } from "../hooks/use-my-team";
import {
  useAlgorithms,
  useDefaultAlgorithmId,
  usePredictions,
} from "../hooks/use-predictions";
import {
  applyPriceFloorToTierBProbabilities,
  determineTier,
  FANTASY_SEASONS,
  getConstructorColor,
  getSeasonPricing,
} from "../lib/constants";
import { findBaselineTeam } from "../lib/my-team";
import {
  type ConstructorBudgetCalculation,
  calculatePriceChangeProbabilities,
  calculatePriceChangeProbabilitiesFromDistribution,
  type DriverBudgetCalculation,
  filterBySearch,
  filterByTier,
  sortByExpectedDelta,
} from "../lib/price-change-engine";

/** "How it works" button + dialog explaining probability calculations. */
function ProbabilityExplainer() {
  return (
    <>
      <Dialog>
        <DialogTrigger asChild>
          <button
            type="button"
            className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors"
          >
            <HelpCircle className="h-3.5 w-3.5" />
            {m.f1_budget_prob_how_it_works()}
          </button>
        </DialogTrigger>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{m.f1_budget_prob_title()}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-foreground/80">
            <p>{m.f1_budget_prob_p1()}</p>
            <p>{m.f1_budget_prob_p2()}</p>
            <p>
              <span className="font-mono font-semibold text-foreground">
                {m.f1_budget_prob_p3_label()}
              </span>{" "}
              {m.f1_budget_prob_p3()}
            </p>
            <p className="text-foreground/60 text-xs">
              {m.f1_budget_prob_p4()}
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Stage ID type - matches the oRPC endpoint (re-export for local use)
type StageId = BudgetBuilderStage;

// Route API for accessing loader data
const routeApi = getRouteApi("/f1-fantasy/budget-boost");

export function BudgetBuilder() {
  // Get SSR-prefetched loader data for initial state
  // This ensures the component renders with the correct values immediately
  const loaderData = routeApi.useLoaderData();
  // Read URL search params — single source of truth for race/stage after client navigation
  const {
    season: urlSeason,
    race: urlRaceSlug,
    stage: urlStage,
    view: urlView,
  } = routeApi.useSearch();

  // Presentation mode — URL-driven so reloads/share links restore the choice.
  const activeView: "detailed" | "compact" = urlView ?? "detailed";

  const [searchQuery, setSearchQuery] = useState("");
  const [stageDropdownOpen, setStageDropdownOpen] = useState(false);

  // Initialize state from loader data (SSR prefetched)
  // These values are determined on the server and passed down
  const [selectedSeason, setSelectedSeason] = useState<number>(
    loaderData.season,
  );

  // When the user changes season, selectedSeason (React state) updates immediately
  // but urlSeason (TanStack Router context) lags one render. During this gap,
  // urlRaceSlug is stale and must not be used as the initial race for the new season.
  const initRaceSlug =
    selectedSeason === (urlSeason ?? loaderData.season)
      ? urlRaceSlug
      : undefined;
  const [selectedRaceSlug, setSelectedRaceSlug] = useState<string | null>(
    loaderData.raceSlug,
  );
  const [manualStage, setManualStage] = useState<StageId | null>(null);

  // Fetch races (SSR prefetched for default season)
  const { data: races } = useRaces(selectedSeason);

  // Derive selectedRace from races data using the slug
  const selectedRace = useMemo<Race | null>(() => {
    if (!races || !selectedRaceSlug) return null;
    return races.find((r) => r.slug === selectedRaceSlug) ?? null;
  }, [races, selectedRaceSlug]);

  // When season changes, keep selectedRaceSlug as null until SeasonRaceSelector
  // fires onRaceChange with the correct default race for the new season.
  const [prevRaces, setPrevRaces] = useState(races);
  if (races !== prevRaces) {
    setPrevRaces(races);
  }

  // Reset manual stage when race changes (render-time state adjustment)
  const [prevRaceSlug, setPrevRaceSlug] = useState(selectedRaceSlug);
  if (selectedRaceSlug !== prevRaceSlug) {
    setPrevRaceSlug(selectedRaceSlug);
    setManualStage(null);
  }

  // Close stage dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest("[data-stage-dropdown]")) {
        setStageDropdownOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // Fetch race stages (SSR prefetched for default race)
  const { data: stagesData } = useRaceStages(
    selectedSeason,
    selectedRaceSlug ?? undefined,
  );

  // Determine effective stage: manual > URL param > API current stage
  const effectiveStage: StageId = useMemo(() => {
    if (manualStage) return manualStage;
    if (urlStage) return urlStage as StageId;
    return (stagesData?.currentStage as StageId) ?? "pre_weekend";
  }, [manualStage, urlStage, stagesData?.currentStage]);

  // Persist race + stage to URL so reloads restore the same selection
  const navigate = useNavigate({ from: "/f1-fantasy/budget-boost" });

  // Handler for SeasonRaceSelector race change
  const handleRaceChange = (race: Race) => {
    // Guard: SeasonRaceSelector fires onRaceChange on initialization (same race).
    // Only clear stage when the user actually picks a different race.
    const isNewRace = race.slug !== selectedRaceSlug;
    setSelectedRaceSlug(race.slug);
    navigate({
      search: (prev) => ({
        ...prev,
        race: race.slug,
        ...(isNewRace ? { stage: undefined } : {}),
      }),
      replace: true,
    });
  };

  // Handler for SeasonRaceSelector season change
  const handleSeasonChange = (season: number) => {
    setSelectedSeason(season);
    setSelectedRaceSlug(null);
    setManualStage(null);
    // Clear race+stage from URL so SeasonRaceSelector auto-picks default for new season
    navigate({ search: { season }, replace: true });
  };

  // Handler for the Detailed / Compact view toggle
  const handleViewChange = (view: "detailed" | "compact") => {
    navigate({
      search: (prev) => ({
        ...prev,
        view: view === "detailed" ? undefined : view,
      }),
      replace: true,
    });
  };

  // Fetch team calculator data — same data layer as Apex Team
  const {
    data: teamData,
    isLoading,
    isFetching,
  } = useTeamCalculatorData(
    selectedSeason,
    selectedRaceSlug ?? undefined,
    effectiveStage,
  );

  // Algorithm selection (same pattern as Apex Team)
  const defaultAlgorithmId = useDefaultAlgorithmId();
  const [selectedAlgorithmId, setSelectedAlgorithmId] =
    useState<string>(defaultAlgorithmId);
  const algorithms = useAlgorithms();

  // Apply prediction algorithm
  const predictions = usePredictions(teamData, selectedAlgorithmId);

  // Highlight the signed-in user's saved team (exact snapshot for this race,
  // or the line-up carried forward from the previous race). BudgetItem rows
  // are keyed by the prediction entity ids, so map saved slugs through
  // predictions.
  const myTeam = useMyTeamVault(selectedSeason);
  const myTeamIds = useMemo(() => {
    if (!myTeam.signedIn || !myTeam.vault || !selectedRace) return null;
    const snapshot =
      myTeam.vault.races[selectedRace.slug] ??
      findBaselineTeam(myTeam.vault, selectedRace.round);
    if (!snapshot) return null;
    const ids = new Set<string>();
    for (const slug of snapshot.drivers) {
      if (!slug) continue;
      const id = predictions.drivers.find((d) => d.driverSlug === slug)?.id;
      if (id) ids.add(id);
    }
    for (const slug of snapshot.constructors) {
      if (!slug) continue;
      const id = predictions.constructors.find((c) => c.slug === slug)?.id;
      if (id) ids.add(id);
    }
    return ids.size > 0 ? ids : null;
  }, [
    myTeam.signedIn,
    myTeam.vault,
    selectedRace,
    predictions.drivers,
    predictions.constructors,
  ]);

  // Show loading only on initial load, not on refetch
  const showLoading = isLoading && !teamData;
  // Race-change in progress (has old data, fetching new data)
  const isRefetching = isFetching && !showLoading;

  // Season pricing rules (price floor + tier threshold): confirmed values for
  // known seasons; the floor is derived from the lowest observed price
  // otherwise (2026 floor is unknown; drivers go as low as $3.2M — far below
  // the old $4.5M floor; the 2026 tier threshold is $18.5M, down from $19M).
  const driverPricing = useMemo(
    () =>
      getSeasonPricing(
        selectedSeason,
        predictions.drivers.map((d) => d.currentPrice),
      ),
    [selectedSeason, predictions.drivers],
  );
  const constructorPricing = useMemo(
    () =>
      getSeasonPricing(
        selectedSeason,
        predictions.constructors.map((c) => c.currentPrice),
      ),
    [selectedSeason, predictions.constructors],
  );

  // Display label for the season's tier threshold (e.g. "$18.5M") — shown in
  // the Tier A/B section subtitles. Same for drivers and constructors.
  const tierThresholdLabel = `$${driverPricing.tierThreshold}M`;

  // Map algorithm predictions to the DriverBudgetCalculation shape the UI expects
  const calculatedDrivers = useMemo((): DriverBudgetCalculation[] => {
    return predictions.drivers.map((driver) => {
      const tier = determineTier(
        driver.currentPrice,
        driverPricing.tierThreshold,
      );
      const history = driver.raceHistory;
      const r1 = history.length >= 1 ? history[history.length - 1] : null;
      const r2 = history.length >= 2 ? history[history.length - 2] : null;
      const ptsR2 = r2?.totalPoints ?? null;
      const ptsR1 = r1?.totalPoints ?? null;
      // Use distribution-based probabilities when MC stdDev is available —
      // this gives accurate P(score >= threshold) instead of a static lookup table.
      // Pass R-2/R-1 points so thresholds account for the 3-race rolling average.
      const rawProbs =
        driver.stdDev && driver.stdDev > 0
          ? calculatePriceChangeProbabilitiesFromDistribution(
              driver.predictedRacePoints,
              driver.stdDev,
              driver.currentPrice,
              tier,
              ptsR2,
              ptsR1,
            )
          : calculatePriceChangeProbabilities(driver.predictedRacePoints, tier);
      const probs =
        tier === "B"
          ? applyPriceFloorToTierBProbabilities(
              driver.currentPrice,
              rawProbs,
              driverPricing.priceFloor,
            )
          : rawProbs;
      return {
        driverId: driver.id,
        driverCode: driver.code, // 3-letter TLA for display
        driverNumber: driver.permanentNumber,
        driverName: driver.fullName,
        constructorName: driver.constructorName,
        constructorColor: getConstructorColor(driver.constructorSlug),
        currentPrice: driver.currentPrice,
        pointsRMinus2: r2?.totalPoints ?? null,
        pointsRMinus1: r1?.totalPoints ?? null,
        expectedPoints: driver.predictedRacePoints,
        priceChangeProbabilities: probs,
        expectedPriceDelta: driver.predictedPriceChange,
        tier,
      };
    });
  }, [predictions.drivers, driverPricing]);

  // Map algorithm predictions to the ConstructorBudgetCalculation shape the UI expects
  const calculatedConstructors = useMemo((): ConstructorBudgetCalculation[] => {
    return predictions.constructors.map((ctor) => {
      const tier = determineTier(
        ctor.currentPrice,
        constructorPricing.tierThreshold,
      );
      const rawProbs = calculatePriceChangeProbabilities(
        ctor.predictedRacePoints,
        tier,
      );
      const probs =
        tier === "B"
          ? applyPriceFloorToTierBProbabilities(
              ctor.currentPrice,
              rawProbs,
              constructorPricing.priceFloor,
            )
          : rawProbs;
      const history = ctor.raceHistory;
      const r1 = history.length >= 1 ? history[history.length - 1] : null;
      const r2 = history.length >= 2 ? history[history.length - 2] : null;
      return {
        constructorId: ctor.id,
        constructorCode: ctor.code,
        constructorName: ctor.name,
        constructorColor: getConstructorColor(ctor.slug),
        currentPrice: ctor.currentPrice,
        pointsRMinus2: r2?.totalPoints ?? null,
        pointsRMinus1: r1?.totalPoints ?? null,
        expectedPoints: ctor.predictedRacePoints,
        priceChangeProbabilities: probs,
        expectedPriceDelta: ctor.predictedPriceChange,
        tier,
      };
    });
  }, [predictions.constructors, constructorPricing]);

  // Filter and sort drivers
  const tierADrivers = useMemo(() => {
    const tierA = filterByTier(calculatedDrivers, "A");
    const filtered = filterBySearch(tierA, searchQuery);
    return sortByExpectedDelta(filtered);
  }, [calculatedDrivers, searchQuery]);

  const tierBDrivers = useMemo(() => {
    const tierB = filterByTier(calculatedDrivers, "B");
    const filtered = filterBySearch(tierB, searchQuery);
    return sortByExpectedDelta(filtered);
  }, [calculatedDrivers, searchQuery]);

  // Filter and sort constructors
  const tierAConstructors = useMemo(() => {
    const tierA = filterByTier(calculatedConstructors, "A");
    const filtered = filterBySearch(tierA, searchQuery);
    return sortByExpectedDelta(filtered);
  }, [calculatedConstructors, searchQuery]);

  const tierBConstructors = useMemo(() => {
    const tierB = filterByTier(calculatedConstructors, "B");
    const filtered = filterBySearch(tierB, searchQuery);
    return sortByExpectedDelta(filtered);
  }, [calculatedConstructors, searchQuery]);

  // Shared entity state (used by both mobile and desktop tabs)
  const [activeEntity, setActiveEntity] = useState<"drivers" | "constructors">(
    "drivers",
  );
  const [mobileTier, setMobileTier] = useState<"A" | "B">("A");
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [earlyWarningDismissed, setEarlyWarningDismissed] = useState(false);

  // Mobile filtered data
  const mobileDrivers = mobileTier === "A" ? tierADrivers : tierBDrivers;
  const mobileConstructors =
    mobileTier === "A" ? tierAConstructors : tierBConstructors;
  const isPredictionMode = predictions.isPredictionMode;

  // Shared stage selector dropdown
  const stageSelectorDropdown = (
    <div className="relative" data-stage-dropdown>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setStageDropdownOpen(!stageDropdownOpen);
        }}
        disabled={!stagesData?.stages?.length}
        className="w-full flex items-center gap-1.5 px-2.5 py-2 rounded-lg bg-muted/50 border border-border text-sm hover:bg-muted/70 transition-colors disabled:opacity-50 disabled:cursor-not-allowed md:w-auto md:min-w-[160px] md:gap-2 md:px-3"
      >
        <Calendar className="h-4 w-4 text-primary flex-shrink-0" />
        <span className="font-medium flex-1 text-left truncate">
          {stagesData?.stages?.find((s) => s.id === effectiveStage)?.name ??
            m.f1_budget_stage_pre_weekend()}
        </span>
        <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
      </button>

      {stageDropdownOpen && stagesData?.stages && (
        <div className="absolute top-full left-0 md:right-0 md:left-auto mt-1 w-72 max-w-[calc(100vw-2rem)] rounded-lg bg-card border border-border shadow-lg z-50 max-h-80 overflow-y-auto">
          <div className="p-2">
            {stagesData.isSprintWeekend && (
              <div className="px-3 py-1.5 mb-2 text-xs font-medium text-amber-700 dark:text-amber-400 bg-amber-500/10 rounded-md flex items-center gap-1.5">
                <Flag className="h-3 w-3" />
                {m.f1_apex_sprint_weekend()}
              </div>
            )}
            {stagesData.stages.map((stage) => {
              const isStageSelected = effectiveStage === stage.id;
              const isDisabled = !stage.isAvailable;

              return (
                <button
                  key={stage.id}
                  type="button"
                  disabled={isDisabled}
                  title={isDisabled ? m.f1_budget_stage_locked() : undefined}
                  onClick={() => {
                    if (!isDisabled) {
                      setManualStage(stage.id as StageId);
                      navigate({
                        search: (prev) => ({
                          ...prev,
                          stage: stage.id,
                        }),
                        replace: true,
                      });
                      setStageDropdownOpen(false);
                    }
                  }}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors flex items-center gap-2 ${
                    isStageSelected
                      ? "bg-primary/20 text-primary"
                      : isDisabled
                        ? "opacity-50 cursor-not-allowed"
                        : "hover:bg-muted/50"
                  }`}
                >
                  <div className="flex-1">
                    <div className="font-medium flex items-center gap-2">
                      {stage.name}
                      {isDisabled && (
                        <Lock className="h-3 w-3 text-muted-foreground" />
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {stage.description}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );

  // Detailed / Compact segmented toggle (shared between mobile + desktop)
  const viewToggle = (
    <div className="flex rounded-lg tab-group p-0.5">
      <button
        type="button"
        onClick={() => handleViewChange("detailed")}
        aria-pressed={activeView === "detailed"}
        className={`flex-1 md:flex-none px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
          activeView === "detailed"
            ? "chip-on"
            : "text-foreground/70 hover:text-foreground"
        }`}
      >
        {m.f1_budget_view_detailed()}
      </button>
      <button
        type="button"
        onClick={() => handleViewChange("compact")}
        aria-pressed={activeView === "compact"}
        className={`flex-1 md:flex-none px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
          activeView === "compact"
            ? "chip-on"
            : "text-foreground/70 hover:text-foreground"
        }`}
      >
        {m.f1_budget_view_compact()}
      </button>
    </div>
  );

  return (
    <div className="" suppressHydrationWarning>
      {/* ================================================================
			    MOBILE LAYOUT (below md:)
			    ================================================================ */}
      <div className="md:hidden">
        {/* Mobile Header */}
        <div className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-16 z-40">
          <div className="px-4 py-3 space-y-2">
            {/* Row 1: back + title + search icon */}
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Link
                  to="/f1-fantasy"
                  className="p-1 -ml-1 text-muted-foreground hover:text-foreground"
                  aria-label={m.common_back_to_f1()}
                >
                  <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                </Link>
                <DollarSign className="h-5 w-5 text-primary" />
                {/* Mobile header label — not an <h1>: the desktop header below
                    is the single page <h1> (avoids two H1s in the DOM). */}
                <div className="font-display text-base font-bold text-foreground">
                  {m.f1_budget_boost_title()}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setMobileSearchOpen(!mobileSearchOpen)}
                aria-label={
                  mobileSearchOpen
                    ? m.common_close_search()
                    : m.common_open_search()
                }
                aria-expanded={mobileSearchOpen}
                className={`p-2 rounded-lg transition-colors ${
                  mobileSearchOpen
                    ? "bg-primary/20 border border-primary/50 text-primary"
                    : "chip-idle text-foreground/70"
                }`}
              >
                <Search className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
            {/* Expandable search bar — immediately below title row */}
            {mobileSearchOpen && (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder={m.f1_budget_search_placeholder()}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-8 py-2 rounded-lg bg-muted/50 border border-border text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery("")}
                    aria-label={m.common_clear_search()}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            )}

            {/* Row 2: Season + Race + refetch spinner */}
            <div className="flex items-center gap-2">
              <SeasonRaceSelector
                defaultSeason={selectedSeason}
                initialRaceSlug={initRaceSlug}
                filterSeasons={FANTASY_SEASONS}
                filterRacesToSelectable
                onSeasonChange={handleSeasonChange}
                onRaceChange={handleRaceChange}
                showRaceSelector={true}
                compact
                className="flex-1 min-w-0"
              />
              {isRefetching && (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground shrink-0" />
              )}
            </div>
            {/* Row 3: Stage + Algorithm selectors */}
            <div className="flex gap-2">
              <div className="flex-1 min-w-0">{stageSelectorDropdown}</div>
              <div className="flex-1 min-w-0">
                <AlgorithmSelector
                  algorithms={algorithms}
                  selectedId={selectedAlgorithmId}
                  onSelect={setSelectedAlgorithmId}
                  className="w-full"
                />
              </div>
            </div>

            {/* Entity + Tier toggles */}
            <div className="flex gap-2">
              <MobileTabBar
                tabs={[
                  {
                    id: "drivers",
                    label: m.f1_apex_drivers(),
                    icon: <Users className="h-3.5 w-3.5" />,
                  },
                  {
                    id: "constructors",
                    label: m.f1_apex_constructors(),
                    icon: <Flag className="h-3.5 w-3.5" />,
                  },
                ]}
                activeTab={activeEntity}
                onTabChange={(id) => {
                  setActiveEntity(id as "drivers" | "constructors");
                  window.scrollTo(0, 0);
                }}
                className="flex-1"
              />
              <MobileTabBar
                tabs={[
                  {
                    id: "A",
                    label: m.f1_budget_tier_a(),
                    icon: undefined,
                  },
                  { id: "B", label: m.f1_budget_tier_b() },
                ]}
                activeTab={mobileTier}
                onTabChange={(id) => {
                  setMobileTier(id as "A" | "B");
                  window.scrollTo(0, 0);
                }}
              />
            </div>

            {/* View toggle: Detailed (bars) vs Compact (table) */}
            {viewToggle}
          </div>
        </div>

        {/* Intro text */}
        <div className="px-4 pt-4">
          <p className="text-sm text-muted-foreground leading-relaxed">
            {m.f1_budget_intro()}
          </p>
        </div>

        {/* Mobile Card Stack */}
        {showLoading && <BudgetBuilderMobileContentSkeleton />}

        <div
          className={`px-4 py-4 space-y-3 transition-opacity duration-200 ${
            isRefetching ? "opacity-40 pointer-events-none" : "opacity-100"
          }`}
        >
          {!showLoading && !selectedRace && (
            <div className="text-center py-16">
              <DollarSign className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground text-sm">
                {m.f1_budget_select_race()}
              </p>
            </div>
          )}

          {/* Mobile early season warning banner */}
          {!showLoading &&
            selectedRace &&
            teamData?.earlySeasonMode &&
            !earlyWarningDismissed && (
              <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-700 dark:text-amber-400">
                <div className="flex items-start gap-2">
                  <span className="flex-1">
                    <span className="font-medium">
                      {m.f1_budget_early_season_label()}
                    </span>{" "}
                    {m.f1_budget_early_season_note()}
                  </span>
                  <button
                    type="button"
                    onClick={() => setEarlyWarningDismissed(true)}
                    aria-label={m.common_dismiss_warning()}
                    className="shrink-0 mt-0.5 text-amber-700/60 hover:text-amber-700 dark:text-amber-400/60 dark:hover:text-amber-400"
                  >
                    <X className="h-4 w-4" aria-hidden="true" />
                  </button>
                </div>
              </div>
            )}

          {/* Mobile legend — shown above cards when there is data */}
          {!showLoading &&
            selectedRace &&
            (mobileDrivers.length > 0 || mobileConstructors.length > 0) && (
              <div className="flex flex-col gap-1.5 text-xs text-muted-foreground border-t border-border/50 pt-3">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="flex items-center gap-1">
                    <span className="inline-block w-2 h-2 rounded-full bg-success" />
                    {m.f1_budget_price_rise()}
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="inline-block w-2 h-2 rounded-full bg-destructive" />
                    {m.f1_budget_price_drop()}
                  </span>
                  <span>{m.f1_budget_legend_xpts()}</span>
                  <span>{m.f1_budget_legend_xdelta()}</span>
                  <ProbabilityExplainer />
                </div>
                <div className="text-muted-foreground">
                  {m.f1_budget_legend_detail()}
                </div>
              </div>
            )}

          {!showLoading &&
            selectedRace &&
            activeEntity === "drivers" &&
            (activeView === "compact" ? (
              <BudgetCompactTable
                items={mobileDrivers}
                isPredictionMode={isPredictionMode}
                myTeamIds={myTeamIds}
              />
            ) : (
              mobileDrivers.map((driver) => (
                <BudgetVerdictCard
                  key={driver.driverId}
                  item={driver}
                  isPredictionMode={isPredictionMode}
                  inMyTeam={myTeamIds?.has(driver.driverId) ?? false}
                />
              ))
            ))}

          {!showLoading &&
            selectedRace &&
            activeEntity === "constructors" &&
            (activeView === "compact" ? (
              <BudgetCompactTable
                items={mobileConstructors}
                isPredictionMode={isPredictionMode}
                myTeamIds={myTeamIds}
              />
            ) : (
              mobileConstructors.map((team) => (
                <BudgetVerdictCard
                  key={team.constructorId}
                  item={team}
                  isPredictionMode={isPredictionMode}
                  inMyTeam={myTeamIds?.has(team.constructorId) ?? false}
                />
              ))
            ))}

          {!showLoading &&
            selectedRace &&
            ((activeEntity === "drivers" && mobileDrivers.length === 0) ||
              (activeEntity === "constructors" &&
                mobileConstructors.length === 0)) && (
              <div className="text-center py-8 text-muted-foreground text-sm">
                {activeEntity === "drivers"
                  ? m.f1_budget_no_drivers()
                  : m.f1_budget_no_constructors()}
              </div>
            )}
        </div>
      </div>

      <AdPlacement className="my-6" />

      {/* ================================================================
			    DESKTOP LAYOUT (md: and above)
			    ================================================================ */}
      <div className="hidden md:block">
        {/* Header */}
        <div className="relative border-b border-border bg-card/50 backdrop-blur-sm sticky top-16 z-40">
          {/* Bottom accent line */}
          <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent" />
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <Link
                  to="/f1-fantasy"
                  className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  <ArrowLeft className="h-4 w-4" />
                  <span className="text-sm">{m.common_back()}</span>
                </Link>
                <div className="w-px h-6 bg-border" />
                <div className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-primary" />
                  <h1 className="font-display text-xl font-bold text-foreground">
                    {m.f1_budget_boost_title()}
                  </h1>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <SeasonRaceSelector
                  defaultSeason={selectedSeason}
                  initialRaceSlug={initRaceSlug}
                  filterSeasons={FANTASY_SEASONS}
                  filterRacesToSelectable
                  onSeasonChange={handleSeasonChange}
                  onRaceChange={handleRaceChange}
                  showRaceSelector={true}
                />
                {isRefetching && (
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                )}
                {stageSelectorDropdown}
                <AlgorithmSelector
                  algorithms={algorithms}
                  selectedId={selectedAlgorithmId}
                  onSelect={setSelectedAlgorithmId}
                />

                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder={m.f1_budget_search_placeholder()}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 pr-8 py-2 w-64 rounded-lg bg-muted/50 border border-border text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery("")}
                      aria-label={m.common_clear_search()}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      <X className="h-4 w-4" aria-hidden="true" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {showLoading && <BudgetBuilderDesktopContentSkeleton />}

        <div
          className={`container mx-auto px-4 py-8 space-y-6 transition-opacity duration-200 ${
            isRefetching ? "opacity-40 pointer-events-none" : "opacity-100"
          }`}
        >
          {/* Intro text */}
          <div className="mb-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              {m.f1_budget_intro()}
            </p>
          </div>

          {/* Early season warning banner */}
          {!showLoading &&
            selectedRace &&
            teamData?.earlySeasonMode &&
            !earlyWarningDismissed && (
              <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-700 dark:text-amber-400">
                <div className="flex items-start gap-2">
                  <span className="flex-1">
                    <span className="font-medium">
                      {m.f1_budget_early_season_label()}
                    </span>{" "}
                    {m.f1_budget_early_season_note()}
                  </span>
                  <button
                    type="button"
                    onClick={() => setEarlyWarningDismissed(true)}
                    aria-label={m.common_dismiss_warning()}
                    className="shrink-0 mt-0.5 text-amber-700/60 hover:text-amber-700 dark:text-amber-400/60 dark:hover:text-amber-400"
                  >
                    <X className="h-4 w-4" aria-hidden="true" />
                  </button>
                </div>
              </div>
            )}

          {!showLoading && !selectedRace && (
            <div className="text-center py-16">
              <DollarSign className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">
                {m.f1_budget_select_race()}
              </p>
            </div>
          )}

          {!showLoading &&
            selectedRace &&
            calculatedDrivers.length === 0 &&
            calculatedConstructors.length === 0 && (
              <div className="text-center py-16">
                <DollarSign className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">{m.f1_budget_no_data()}</p>
                <p className="text-sm text-muted-foreground mt-2">
                  {m.f1_budget_no_data_detail()}
                </p>
              </div>
            )}

          {!showLoading &&
            (calculatedDrivers.length > 0 ||
              calculatedConstructors.length > 0) && (
              <div className="space-y-6">
                {/* Entity tabs + view toggle + legend */}
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="flex rounded-lg tab-group p-0.5">
                      <button
                        type="button"
                        onClick={() => setActiveEntity("drivers")}
                        className={`flex items-center gap-2 px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                          activeEntity === "drivers"
                            ? "chip-on"
                            : "text-foreground/70 hover:text-foreground"
                        }`}
                      >
                        <Users className="h-4 w-4" />
                        {m.f1_apex_drivers()}
                      </button>
                      <button
                        type="button"
                        onClick={() => setActiveEntity("constructors")}
                        className={`flex items-center gap-2 px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                          activeEntity === "constructors"
                            ? "chip-on"
                            : "text-foreground/70 hover:text-foreground"
                        }`}
                      >
                        <Flag className="h-4 w-4" />
                        {m.f1_apex_constructors()}
                      </button>
                    </div>
                    {viewToggle}
                  </div>
                  {/* Legend — desktop only */}
                  <div className="hidden md:flex items-center gap-3 flex-wrap text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <span className="inline-block w-2 h-2 rounded-full bg-success" />
                      {m.f1_budget_price_rise()}
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="inline-block w-2 h-2 rounded-full bg-destructive" />
                      {m.f1_budget_price_drop()}
                    </span>
                    <span className="text-muted-foreground">
                      {m.f1_budget_legend_xpts()}
                    </span>
                    <span className="text-muted-foreground">
                      {m.f1_budget_legend_xdelta()}
                    </span>
                    <span className="text-muted-foreground">
                      {m.f1_budget_legend_rpts()}
                    </span>
                    <span className="text-muted-foreground">
                      {m.f1_budget_legend_r1r2()}
                    </span>
                    <ProbabilityExplainer />
                  </div>
                </div>

                {/* Tier A section (gated) — shown first */}
                {activeEntity === "drivers" && tierADrivers.length > 0 && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <h2 className="text-base font-semibold">
                        {m.f1_budget_tier_a_drivers_title()}
                      </h2>
                      <span className="text-xs text-muted-foreground bg-muted/50 px-2 py-0.5 rounded-full">
                        {m.f1_budget_tier_a_drivers_subtitle({
                          threshold: tierThresholdLabel,
                        })}
                      </span>
                    </div>

                    <Suspense
                      fallback={<BudgetBuilderDesktopContentSkeleton />}
                    >
                      {activeView === "compact" ? (
                        <BudgetCompactTable
                          items={tierADrivers}
                          isPredictionMode={isPredictionMode}
                          myTeamIds={myTeamIds}
                        />
                      ) : (
                        <div className="space-y-2">
                          {tierADrivers.map((driver) => (
                            <BudgetVerdictCard
                              key={driver.driverId}
                              item={driver}
                              isPredictionMode={isPredictionMode}
                              inMyTeam={
                                myTeamIds?.has(driver.driverId) ?? false
                              }
                            />
                          ))}
                        </div>
                      )}
                    </Suspense>
                  </div>
                )}

                {activeEntity === "constructors" &&
                  tierAConstructors.length > 0 && (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <h2 className="text-base font-semibold">
                          {m.f1_budget_tier_a_constructors_title()}
                        </h2>
                        <span className="text-xs text-muted-foreground bg-muted/50 px-2 py-0.5 rounded-full">
                          {m.f1_budget_tier_a_constructors_subtitle({
                            threshold: tierThresholdLabel,
                          })}
                        </span>
                      </div>

                      <Suspense
                        fallback={<BudgetBuilderDesktopContentSkeleton />}
                      >
                        {activeView === "compact" ? (
                          <BudgetCompactTable
                            items={tierAConstructors}
                            isPredictionMode={isPredictionMode}
                            myTeamIds={myTeamIds}
                          />
                        ) : (
                          <div className="space-y-2">
                            {tierAConstructors.map((team) => (
                              <BudgetVerdictCard
                                key={team.constructorId}
                                item={team}
                                isPredictionMode={isPredictionMode}
                                inMyTeam={
                                  myTeamIds?.has(team.constructorId) ?? false
                                }
                              />
                            ))}
                          </div>
                        )}
                      </Suspense>
                    </div>
                  )}

                <AdPlacement className="my-6" />

                {/* Tier B section (free) — shown below Tier A */}
                {activeEntity === "drivers" && tierBDrivers.length > 0 && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <h2 className="text-base font-semibold">
                        {m.f1_budget_tier_b_drivers_title()}
                      </h2>
                      <span className="text-xs text-muted-foreground bg-muted/50 px-2 py-0.5 rounded-full">
                        {m.f1_budget_tier_b_drivers_subtitle({
                          threshold: tierThresholdLabel,
                        })}
                      </span>
                    </div>
                    <Suspense
                      fallback={<BudgetBuilderDesktopContentSkeleton />}
                    >
                      {activeView === "compact" ? (
                        <BudgetCompactTable
                          items={tierBDrivers}
                          isPredictionMode={isPredictionMode}
                          myTeamIds={myTeamIds}
                        />
                      ) : (
                        <div className="space-y-2">
                          {tierBDrivers.map((driver) => (
                            <BudgetVerdictCard
                              key={driver.driverId}
                              item={driver}
                              isPredictionMode={isPredictionMode}
                              inMyTeam={
                                myTeamIds?.has(driver.driverId) ?? false
                              }
                            />
                          ))}
                        </div>
                      )}
                    </Suspense>
                  </div>
                )}

                {activeEntity === "constructors" &&
                  tierBConstructors.length > 0 && (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <h2 className="text-base font-semibold">
                          {m.f1_budget_tier_b_constructors_title()}
                        </h2>
                        <span className="text-xs text-muted-foreground bg-muted/50 px-2 py-0.5 rounded-full">
                          {m.f1_budget_tier_b_constructors_subtitle({
                            threshold: tierThresholdLabel,
                          })}
                        </span>
                      </div>
                      <Suspense
                        fallback={<BudgetBuilderDesktopContentSkeleton />}
                      >
                        {activeView === "compact" ? (
                          <BudgetCompactTable
                            items={tierBConstructors}
                            isPredictionMode={isPredictionMode}
                            myTeamIds={myTeamIds}
                          />
                        ) : (
                          <div className="space-y-2">
                            {tierBConstructors.map((team) => (
                              <BudgetVerdictCard
                                key={team.constructorId}
                                item={team}
                                isPredictionMode={isPredictionMode}
                                inMyTeam={
                                  myTeamIds?.has(team.constructorId) ?? false
                                }
                              />
                            ))}
                          </div>
                        )}
                      </Suspense>
                    </div>
                  )}
              </div>
            )}
        </div>
      </div>

      <F1Explainer
        title={m.f1_bb_title()}
        howTo={m.f1_bb_howto()}
        faqs={[
          { q: m.f1_bb_faq1_q(), a: m.f1_bb_faq1_a() },
          { q: m.f1_bb_faq2_q(), a: m.f1_bb_faq2_a() },
          { q: m.f1_bb_faq3_q(), a: m.f1_bb_faq3_a() },
          { q: m.f1_bb_faq4_q(), a: m.f1_bb_faq4_a() },
        ]}
      />
      <AdPlacement className="mt-6" />
    </div>
  );
}
